import React from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  Box,
  Button,
  TextField,
  Typography,
  Select,
  MenuItem,
  Paper,
  Grid,
  Card,
  CardContent,
} from "@mui/material";

const API_BASE = process.env.REACT_APP_API_URL;

export default function DashboardPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  const farmerData = JSON.parse(localStorage.getItem("farmerData") || "{}");
  const farmerUsername = localStorage.getItem("farmerUsername") || "";

  const [dateRanges, setDateRanges] = React.useState([]);
  const [selectedRange, setSelectedRange] = React.useState("");
  const [mainBill, setMainBill] = React.useState(null);
  const [naveDetails, setNaveDetails] = React.useState({});

  const formatCurrency = (value) => {
    if (!value || isNaN(value)) return "₹0.00";
    return `₹${Number(value).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
    })}`;
  };

  React.useEffect(() => {
    if (!farmerUsername) return;

    fetch(`${API_BASE}/bill-summary`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ username: farmerUsername }),
    })
      .then((res) => res.json())
      .then((data) => {
        setDateRanges(data.date_ranges || []);
        if (data.date_ranges && data.date_ranges.length > 0) {
          setSelectedRange(
            JSON.stringify({
              bill_fromdate: data.date_ranges[0].bill_fromdate,
              bill_todate: data.date_ranges[0].bill_todate,
            })
          );
        }
      })
      .catch((err) => console.error("Bill summary fetch error:", err));
  }, [farmerUsername]);

  React.useEffect(() => {
    if (!selectedRange) return;

    const { bill_fromdate, bill_todate } = JSON.parse(selectedRange);

    fetch(`${API_BASE}/bill-details`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        username: farmerUsername,
        bill_fromdate,
        bill_todate,
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        setMainBill(data.main_bill || null);
        // setNaveDetails(data.nave_details || null);
        setNaveDetails((data.nave_details && data.nave_details.length > 0) ? data.nave_details[0] : {});

      })
      .then(() => {
        console.log("Nave Details:", naveDetails);
      })

      .catch((err) => console.error("Bill details fetch error:", err));
  }, [selectedRange, farmerUsername]);

  const handleLogout = () => {
    localStorage.clear();
    navigate("/");
  };

  const Section = ({ title, children }) => (
    <Paper
      elevation={3}
      sx={{
        mb: 3,
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          p: 1.5,
          background: "linear-gradient(45deg, #2e7d32, #66bb6a)",
          color: "#fff",
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
          {title} 
        </Typography>
      </Box>
      <Box sx={{ p: 2 }}>{children}</Box>
    </Paper>
  );

  const SummaryCard = ({ label, value }) => (
    <Card
      elevation={3}
      sx={{
        borderRadius: 2,
        "&:hover": { boxShadow: 6 },
      }}
    >
      <CardContent>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {label}
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: "bold", color: "#2e7d32" }}>
          {value}
        </Typography>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ p: isMobile ? 2 : 4, backgroundColor: "#f5f9f6", minHeight: "100vh" }}>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: "bold", color: "#2e7d32" }}>
        MilkPrO Farmer App
      </Typography>
      {/* <Typography variant="h5" gutterBottom sx={{ fontWeight: "bold", color: "#2e7d32" }}>
        दूध उत्पादक-पुरवठेदार डॅशबोर्ड
      </Typography> */}
      {/* Farmer Code */}
      <TextField
        fullWidth
        label="शेतकरी कोड"
        value={farmerUsername}
        size="small"
        InputProps={{ readOnly: true, style: { fontWeight: "bold" } }}
        sx={{ mb: 2, backgroundColor: "#fff", borderRadius: 1 }}
      />

      {/* Farmer Name */}
      <TextField
        fullWidth
        label="शेतकरी नाव"
        value={
          farmerData.farmer_name ||
          `${farmerData.farmer_firstname || ""} ${farmerData.farmer_lastname || ""}`
        }
        size="small"
        InputProps={{ readOnly: true, style: { fontWeight: "bold" } }}
        sx={{ mb: 3, backgroundColor: "#fff", borderRadius: 1 }}
      />

      {/* Date Range Dropdown */}
      <Section title="बिल दिनांक श्रेणी निवडा">
        <Select
          fullWidth
          size="small"
          value={selectedRange}
          onChange={(e) => setSelectedRange(e.target.value)}
        >
          {dateRanges.map((range, idx) => (
            <MenuItem
              key={idx}
              value={JSON.stringify({
                bill_fromdate: range.bill_fromdate,
                bill_todate: range.bill_todate,
              })}
            >
              {range.label}
            </MenuItem>
          ))}
        </Select>
      </Section>

      {mainBill && (
        <>
          {/* Collection */}
          <Section title="संकलन">
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <SummaryCard label="गाय लिटर्स" value={mainBill.cowqty} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <SummaryCard label="गाय रक्कम" value={formatCurrency(mainBill.cowamt)} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <SummaryCard label="म्हैस लिटर्स" value={mainBill.buffqty} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <SummaryCard label="म्हैस रक्कम" value={formatCurrency(mainBill.buffamt)} />
              </Grid>
            </Grid>
          </Section>

          {/* Billing */}
          <Section title="बिलिंग">
            <Card elevation={4} sx={{ borderRadius: 2, overflow: "hidden" }}>
              {/* Gross Amount */}
              <Box sx={{ backgroundColor: "#f0f0f0", p: 1.5, display: "flex", justifyContent: "space-between", fontWeight: "bold" }}>
                <span>एकूण रक्कम</span>
                <span>{formatCurrency(mainBill.EkunKharedi)}</span>
              </Box>

              {/* Deductions */}
              <Box sx={{ backgroundColor: "#e3f2fd", p: 1.5 }}>
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                  कपाती
                </Typography>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <span>ठेव</span>
                  <span>{formatCurrency(Number(naveDetails.deposit_kapat) || 0)}</span>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <span>पशूखाद्य</span>
                  <span>{formatCurrency(naveDetails.uchalminus2 || 0)}</span>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <span>अॅडव्हान्स</span>
                  <span>{formatCurrency(naveDetails.uchalminus3 || 0)}</span>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <span>इतर कपात</span>
                  <span>{formatCurrency(naveDetails.uchalminus4 || 0)}</span>
                </Box>
              </Box>

              {/* Net Bill */}
              <Box sx={{ backgroundColor: "#e8f5e9", p: 1.5, display: "flex", justifyContent: "space-between", borderTop: "2px solid #4caf50" }}>
                <Typography sx={{ fontWeight: "bold", color: "#2e7d32" }}>
                  निव्वळ बिल
                </Typography>
                <Typography sx={{ fontWeight: "bold", color: "#2e7d32" }}>
                  {formatCurrency(mainBill.NetBill)}
                </Typography>
              </Box>
            </Card>
          </Section>

          {/* Previous Balance */}
          <Section title="मागील शिल्लक">
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <SummaryCard label="ठेव" value={formatCurrency(mainBill.previousdeposit)} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <SummaryCard label="चारा" value={formatCurrency(mainBill.previouscattlefeed || 0)} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <SummaryCard label="अॅडव्हान्स" value={formatCurrency(mainBill.previousadvance || 0)} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <SummaryCard label="इतर" value={formatCurrency(mainBill.previousother || 0)} />
              </Grid>
            </Grid>
          </Section>

          {/* Current Balance */}
          <Section title="सध्याची शिल्लक (कपातीनंतर)">
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <SummaryCard label="ठेव" value={formatCurrency(mainBill.currentdeposit)} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <SummaryCard label="चारा" value={formatCurrency(mainBill.currentcattlefeed || 0)} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <SummaryCard label="अॅडव्हान्स" value={formatCurrency(mainBill.currentadvance || 0)} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <SummaryCard label="इतर" value={formatCurrency(mainBill.currentother || 0)} />
              </Grid>
            </Grid>
          </Section>
        </>
      )}

      {/* Logout */}
      <Button
        variant="contained"
        sx={{
          backgroundColor: "#2e7d32",
          "&:hover": { backgroundColor: "#1b5e20" },
          mt: 2,
        }}
        onClick={handleLogout}
      >
        लॉगआउट
      </Button>
    </Box>
  );
}
